import React from 'react';
import './EnhancedCheckbox.css';

/**
 * Enhanced Checkbox Component with visual feedback and animations
 * 
 * @param {Object} props - Component props
 * @param {string} props.inputId - Unique ID for the checkbox input
 * @param {string} props.name - Name attribute for the checkbox
 * @param {string} props.className - Additional CSS classes
 * @param {any} props.value - Value of the checkbox
 * @param {function} props.onChange - Change event handler
 * @param {boolean} props.checked - Checked state
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.size - Size variant: 'small', 'medium', 'large' (default: 'medium')
 */
const EnhancedCheckbox = ({
  inputId,
  name,
  className = '',
  value,
  onChange,
  checked = false,
  disabled = false,
  size = 'medium',
  ...rest
}) => {
  const handleChange = (e) => {
    if (!disabled && onChange) {
      onChange({
        checked: e.target.checked,
        value: value,
        originalEvent: e
      });
    }
  };

  const sizeClass = `enhanced-checkbox--${size}`;
  const checkedClass = checked ? 'enhanced-checkbox--checked' : '';
  const disabledClass = disabled ? 'enhanced-checkbox--disabled' : '';

  return (
    <div className={`enhanced-checkbox-wrapper ${className}`}>
      <label 
        htmlFor={inputId} 
        className={`enhanced-checkbox ${sizeClass} ${checkedClass} ${disabledClass}`}
        role="checkbox"
        aria-checked={checked}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            if (!disabled) {
              handleChange({ target: { checked: !checked } });
            }
          }
        }}
      >
        <input
          type="checkbox"
          id={inputId}
          name={name}
          value={value}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="enhanced-checkbox__input"
          tabIndex={-1}
          {...rest}
        />
        
        <span className="enhanced-checkbox__box">
          {/* Checkmark SVG with animation */}
          <svg 
            className="enhanced-checkbox__checkmark" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="enhanced-checkbox__checkmark-path"
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          
          {/* Ripple effect on click */}
          <span className="enhanced-checkbox__ripple"></span>
        </span>
      </label>
    </div>
  );
};

export default EnhancedCheckbox;

